// ─── School Adventure content pack (Adventure 3, 2026-08-17) ───
// Source of truth: "School Adventure — Speaking Tutor Q&A" Google Doc
// (Natalya's UPDATED script, shared 2026-08-17 — the audio freeze point,
// per her same-day email answering the open decisions:
//   1. yes/no ALWAYS appears; any school supply works for "Do you have…?"
//   2. "Do you like to learn?" closes every session (the Farm-favorite slot)
//   3. images: Drive folder 1JkKJQAiTLZN-TyDmpc_JQlUaB41qE9Pv).
//
// FIRST adventure with new question TYPES — the engine grew four pieces for
// this pack (all gated on pack fields, so Farm/Playground are untouched):
//   • `opener`  — unscored "What is your name?" name capture (stores the name)
//   • 'yesno'   tier — "Do you have a ___?"; yes AND no both earn the star,
//                the sentence ladder then teaches the full sentence
//   • 'feelings' tier — "Do you like to learn?"; Frog reacts (Yay! / head down)
//   • `closing` — 3+ stars → personalized "You are a good student, [name]!"
//                (live Puck TTS with the stored name; nameless fallback baked)
//
// Session shape (FIXED slots, not a random pool — script's Question flow):
//   opener + [school, classroom, teacher, student, shape, shape,
//             supply?, supply?, alphabet, do-you-like-to-learn]
//   Shapes: pick 2 of the bank. Supplies: pick 2 of the bank. No repeats.
//
// 2026-08-18: Natalya delivered the last assets (pen, paper, star, the
// do-you-like-to-learn picture, and the classroom background) — full banks
// now: 6 shapes, 13 supplies.

const WHO = { audio: '/audio/who-is-this.mp3', text: 'Who is this?' };
const WHAT = { audio: '/audio/what-is-this.mp3', text: 'What is this?' };

// Article rules from the script: "This is a school." / "This is an oval." /
// "This is the alphabet." / "Do you have glue?" (no article) /
// "Do you have scissors?" (plural, no article).
const art = (c) => (c.article === '' ? '' : `${c.article ?? 'a'} `);

// ── Naming cards (tiers: ['name']) — slots 1-6 and 9 ──
const namingPool = [
  { id: 'sch-school', image: '/images/school/School.webp', word: 'school', plural: 'schools', count: 1, nameOnly: true },
  { id: 'sch-classroom', image: '/images/school/Classroom.webp', word: 'classroom', plural: 'classrooms', count: 1, nameOnly: true, extraWords: ['class room'] },
  { id: 'sch-teacher', image: '/images/school/Teacher.webp', word: 'teacher', plural: 'teachers', count: 1, nameOnly: true, ask: WHO },
  { id: 'sch-student', image: '/images/school/Student.webp', word: 'student', plural: 'students', count: 1, nameOnly: true, ask: WHO, extraWords: ['kid'] },
  { id: 'sch-alphabet', image: '/images/school/Alphabet.png', word: 'alphabet', plural: 'alphabets', count: 1, nameOnly: true, article: 'the', extraWords: ['abc', 'abcs', 'letters'] },
];

// SHAPES bank (script: pick 2 different per session, bank of 6).
const shapesBank = [
  { id: 'sch-circle', image: '/images/school/Circle.png', word: 'circle', plural: 'circles', count: 1, nameOnly: true, extraWords: ['round'] },
  { id: 'sch-square', image: '/images/school/Square.png', word: 'square', plural: 'squares', count: 1, nameOnly: true },
  { id: 'sch-triangle', image: '/images/school/Triangle.webp', word: 'triangle', plural: 'triangles', count: 1, nameOnly: true },
  { id: 'sch-rectangle', image: '/images/school/Rectangle.png', word: 'rectangle', plural: 'rectangles', count: 1, nameOnly: true },
  { id: 'sch-oval', image: '/images/school/Oval.png', word: 'oval', plural: 'ovals', count: 1, nameOnly: true, article: 'an' },
  { id: 'sch-star', image: '/images/school/Star.webp', word: 'star', plural: 'stars', count: 1, nameOnly: true }, // image added 2026-08-18
];

// SCHOOL SUPPLIES bank — "Do you have a ___?" (script: pick 2 per session,
// bank of 13). Both yes and no earn the star.
const suppliesBank = [
  { id: 'sch-book', image: '/images/school/Book.webp', word: 'book', plural: 'books' },
  { id: 'sch-pencil', image: '/images/school/Pencil.webp', word: 'pencil', plural: 'pencils' },
  { id: 'sch-notebook', image: '/images/school/Notebook.webp', word: 'notebook', plural: 'notebooks' },
  { id: 'sch-backpack', image: '/images/school/Backpack.webp', word: 'backpack', plural: 'backpacks' },
  { id: 'sch-ruler', image: '/images/school/Ruler.webp', word: 'ruler', plural: 'rulers' },
  { id: 'sch-glue', image: '/images/school/Glue.webp', word: 'glue', plural: 'glue', article: '' },
  { id: 'sch-marker', image: '/images/school/Marker.webp', word: 'marker', plural: 'markers' },
  { id: 'sch-map', image: '/images/school/Map.webp', word: 'map', plural: 'maps' },
  { id: 'sch-computer', image: '/images/school/Computer.webp', word: 'computer', plural: 'computers' },
  { id: 'sch-eraser', image: '/images/school/Eraser.webp', word: 'eraser', plural: 'erasers', article: 'an' },
  { id: 'sch-scissors', image: '/images/school/Scissors.webp', word: 'scissors', plural: 'scissors', article: '' },
  { id: 'sch-pen', image: '/images/school/Pen.jpg', word: 'pen', plural: 'pens' },              // image added 2026-08-18
  { id: 'sch-paper', image: '/images/school/Paper.webp', word: 'paper', plural: 'paper', article: '' }, // image added 2026-08-18
];

// Slot 10 — "Do you like to learn?" Frog reacts to either answer.
// Dedicated picture from Natalya (kids raising hands, 2026-08-18) replaced
// the temporary classroom reuse.
const learnCard = {
  id: 'sch-learn', image: '/images/school/Learn.jpg',
  word: 'learn', plural: 'learn', count: 1,
};

const pool = [...namingPool, ...shapesBank, ...suppliesBank, learnCard];

// Named exports for tts/gen-school-audio.mjs — the generator derives every
// frog line from these banks via the pack's own builders (no drift possible).
export { namingPool, shapesBank, suppliesBank, learnCard };

const pick = (arr, n) => [...arr].sort(() => Math.random() - 0.5).slice(0, n);

// FIXED slot order per the script's Question-flow table (green = fixed,
// amber = randomized bank draw). Always 10 questions + the unscored opener.
function buildDeck() {
  const by = (id) => namingPool.find((c) => c.id === id);
  const [shape1, shape2] = pick(shapesBank, 2);
  const [supply1, supply2] = pick(suppliesBank, 2);
  return [
    { ...by('sch-school'), tiers: ['name'] },
    { ...by('sch-classroom'), tiers: ['name'] },
    { ...by('sch-teacher'), tiers: ['name'] },
    { ...by('sch-student'), tiers: ['name'] },
    { ...shape1, tiers: ['name'] },
    { ...shape2, tiers: ['name'] },
    { ...supply1, tiers: ['yesno'] },
    { ...supply2, tiers: ['yesno'] },
    { ...by('sch-alphabet'), tiers: ['name'] },
    { ...learnCard, tiers: ['feelings'] },
  ];
}

// ─── String builders (engine prefers these when present) ───
function q(card, tier) {
  if (tier === 'yesno') {
    return { audio: `/audio/${card.id}-have-ask.mp3`, text: `Do you have ${art(card)}${card.word}?` };
  }
  if (tier === 'feelings') {
    return { audio: '/audio/sch-learn-ask.mp3', text: 'Do you like to learn?' };
  }
  // name tier: card override (Who is this?) or the shared default.
  return card.ask || WHAT;
}

function sentence(card, tier) {
  if (tier !== 'name') return null; // yesno sentences depend on the answer → yesNoSentence
  return { sentence: `This is ${art(card)}${card.word}.`, highlight: card.word };
}

function correctLine(card, tier) {
  if (tier !== 'name') return null;
  return { audio: `/audio/${card.id}-correct.mp3`, text: `Yes! ${sentence(card, tier).sentence}` };
}

function revealLine(card, tier) {
  if (tier !== 'name') return null;
  const s = sentence(card, tier).sentence;
  return { audio: `/audio/${card.id}-reveal.mp3`, text: `${s} Repeat after me. ${s}` };
}

// The full sentence the yes/no ladder teaches, per the child's OWN answer
// ("Yes, I have a pencil." / "No, I do not have a pencil." — script wording).
function yesNoSentence(card, saidYes) {
  const noun = `${art(card)}${card.word}`;
  return saidYes
    ? { sentence: `Yes, I have ${noun}.`, highlight: card.word }
    : { sentence: `No, I do not have ${noun}.`, highlight: card.word };
}

// Final-step reveal audio for the ladder (speaks the sentence, then asks for
// the echo — same shape as the name-tier reveals).
function yesNoReveal(card, saidYes) {
  const s = yesNoSentence(card, saidYes).sentence;
  return { audio: `/audio/${card.id}-have-${saidYes ? 'yes' : 'no'}-reveal.mp3`, text: `${s} Repeat after me. ${s}` };
}

const namingWords = [...namingPool, ...shapesBank].map((c) => c.word).join(', ');

export default {
  id: 'school',
  title: 'School Adventure',
  pool,
  buildDeck,
  q, sentence, correctLine, revealLine,
  yesNoSentence, yesNoReveal,

  // ── Unscored name-capture opener (script: always first) ──
  opener: {
    // The greeting IS the opener question — one line, per the script.
    greeting: { audio: '/audio/sch-greeting.mp3', text: "Hi kids, let's practice speaking! My name is Frog. What is your name?" },
    question: 'What is your name?',
    greatName: { audio: '/audio/sch-great-name.mp3', text: "That's a great name!" },
    sttContext: "a young child saying their first name, maybe as 'my name is …'",
  },

  // ── Feelings reactions (slot 10) ──
  feelings: {
    yes: { audio: '/audio/sch-yay.mp3', text: 'Yay!' },
    no: { audio: '/audio/sch-sorry.mp3', text: 'I am sorry to hear that.' },
  },

  // ── Personalized closing (3+ stars) ──
  closing: {
    minStars: 3,
    line: (name) => (name ? `You are a good student, ${name}!` : 'You are a good student!'),
    // Nameless fallback is pre-baked; named lines are live Puck (S3-cached).
    audioNoName: '/audio/sch-good-student.mp3',
  },

  hasFavorite: false,
  // Cartoon classroom scene from Natalya (2026-08-18), downscaled from her
  // 5504x3072 source to 1672px wide / q80 jpeg — same treatment as the Farm
  // and Playground backgrounds.
  background: '/images/school-bg.webp', // v3 2026-10-20: open floor so the frog stands on it (tracker #4)

  sttNameContext: `a school word (${namingWords})`,
  sttYesNoContext: 'a yes or no answer from a young child, maybe as a full sentence like "yes, I have a pencil"',
  judgeTopic: 'a school picture card — places and people (school, classroom, teacher, student), '
    + 'shapes (circle, square, triangle, rectangle, oval, star), school supplies, and the alphabet',
  judgeHints: 'Spanish: escuela=school, salón/aula/clase=classroom, maestra/maestro/profesor=teacher, '
    + 'estudiante/alumno=student, círculo=circle, cuadrado=square, triángulo=triangle, '
    + 'rectángulo=rectangle, óvalo=oval, abecedario/alfabeto=alphabet. '
    + 'Russian: школа=school, класс=classroom, учитель/учительница=teacher, ученик/ученица=student, '
    + 'круг=circle, квадрат=square, треугольник=triangle, прямоугольник=rectangle, овал=oval, '
    + 'алфавит=alphabet',
};
